import { createSelector } from '@reduxjs/toolkit'
import {
  SECONDS_IN_MINUTE,
  TTS_BULK_DEFAULT_SPEED,
  TTS_DEFAULT_BULK_INSTRUCTION,
  TTS_DEFAULT_BULK_LANG_CODE,
  TTS_DEFAULT_BULK_SPEAKER_ID,
  TTS_DEFAULT_INSTRUCTION,
  TTS_DEFAULT_LANG_CODE,
  TTS_DEFAULT_SPEAKER_ID,
  TTS_DEFAULT_SPEED,
  TTS_GENERATION_STATUS,
  TTS_ITEM_DATA_CHANGEABLE_KEYS
} from 'consts/tts'
import { TRootState } from 'store/types'
import {
  ITtsItem,
  ITtsItemData,
  TTtsGenerationStatus
} from 'types/text-to-speech'
import {
  determineBulkValue,
  findTtsItemById,
  getIsTtsItemUnchanged
} from './helpers'
import { SUB_SIDEBAR_KEYS } from 'consts/sidebar'
import { editorSelectors } from 'store/features/editor/selectors'
import { INewGeneratedItemsInitialIdAndStatus } from 'store/types/tts'
import { getSortedTtsItemIdsFromAudios } from 'utils/tts'
import { projectDataSelectors } from 'store/features/projectData/selectors'
import { checkIfPremiumVoiceSelected } from './helpers'

const selector = (state: TRootState) => state.tts

const ttsInitialItems = createSelector(
  selector,
  state => state.ttsInitialItems.data
)

const isTtsInitialItemsLoading = createSelector(
  selector,
  state => state.ttsInitialItems.isLoading
)

const ttsItems = createSelector(selector, state => state.ttsMutableItems.data)

const isTtsItemsLoading = createSelector(
  selector,
  state => state.ttsMutableItems.isLoading
)

const isLanguageOptionsLoading = createSelector(
  selector,
  state => state.languageOptions.isLoading
)

const languageOptionsData = createSelector(
  selector,
  state => state.languageOptions.data
)

const speakersPerLangCode = createSelector(
  selector,
  state => state.speakersPerLangCode
)

const makeSpeakersSelectorPerLangCode = () => {
  const selectSpeakersPerLangCode = createSelector(
    [selector, (_state: TRootState, langCode: string) => langCode],
    (ttsState, langCode) => {
      return ttsState.speakersPerLangCode?.[langCode]?.data || null
    }
  )

  return selectSpeakersPerLangCode
}

const makeSpeakersPerLangCodeIsLoadingSelector = () => {
  const selectSpeakersPerLangCodeIsLoading = createSelector(
    [selector, (_state: TRootState, langCode: string) => langCode],
    (ttsState, langCode) =>
      ttsState.speakersPerLangCode?.[langCode]?.isLoading || false
  )

  return selectSpeakersPerLangCodeIsLoading
}

const speakersPerLangCodeSelector = makeSpeakersSelectorPerLangCode()
const speakersPerLangCodeIsLoadingSelector =
  makeSpeakersPerLangCodeIsLoadingSelector()

const speakerTones = createSelector(selector, state => state.speakerTones.data)

const isSpeakerTonesLoading = createSelector(
  selector,
  state => state.speakerTones.isLoading
)

const makeTtsItemById = () =>
  createSelector(
    (_: TRootState, id: number) => id,
    ttsItems,
    (id, items): ITtsItem | undefined => findTtsItemById(items, id)
  )

const isSingleDraftTtsItemOnly = createSelector(
  ttsItems,
  (ttsItems): boolean => ttsItems.length === 1 && !!ttsItems[0]?.isDraft
)

const makeInitialTtsItemById = () =>
  createSelector(
    (_: TRootState, id: number) => id,
    ttsInitialItems,
    (id, items): ITtsItemData | undefined => findTtsItemById(items, id)?.data
  )

const makeTtsItemDataChangedKeysBesidesCurrentKey = (
  currentKey: keyof ITtsItemData
) =>
  createSelector(
    (_: TRootState, id: number) => id,
    makeTtsItemById(),
    makeInitialTtsItemById(),
    (_, ttsItem, initialTtsItem): string[] => {
      const ttsItemData = ttsItem?.data

      if (!ttsItemData || !initialTtsItem) {
        return []
      }

      return Object.keys(TTS_ITEM_DATA_CHANGEABLE_KEYS).filter(
        key =>
          key !== currentKey &&
          ttsItemData[TTS_ITEM_DATA_CHANGEABLE_KEYS[key]] !==
            initialTtsItem[TTS_ITEM_DATA_CHANGEABLE_KEYS[key]]
      )
    }
  )

// if any of the data key values has been already changed, besides the current key,
// we don't need to check the current key to change the generated state
const makeTtsItemOtherKeysAlreadyChanged = (currentKey: keyof ITtsItemData) =>
  createSelector(
    (_: TRootState, id: number) => id,
    makeTtsItemDataChangedKeysBesidesCurrentKey(currentKey),
    isTtsItemTextEmpty,
    (_, ttsItemDataChangedKeys, isTtsItemTextEmpty): boolean => {
      // any changes doesnt mean that the item is ready to be generated
      // we need to check if the text is empty
      if (
        isTtsItemTextEmpty &&
        currentKey !== TTS_ITEM_DATA_CHANGEABLE_KEYS.text
      ) {
        return true
      }
      return !!ttsItemDataChangedKeys.length
    }
  )

const makeTtsItemLoadingStateById = () =>
  createSelector(
    (_: TRootState, id: number) => id,
    makeTtsItemById(),
    (_, ttsItem): boolean => !!ttsItem?.isLoading
  )

const makeTtsItemGeneratedStateById = () =>
  createSelector(
    (_: TRootState, id: number) => id,
    makeTtsItemById(),
    (_, ttsItem): boolean => !!ttsItem?.isGenerated
  )

const makeTtsItemDraftStateById = () =>
  createSelector(
    (_: TRootState, id: number) => id,
    makeTtsItemById(),
    (_, ttsItem): boolean => !!ttsItem?.isDraft
  )

//initial tts data from audios, not from tts items changes

const makeInitialTtsItemTextById = () =>
  createSelector(
    (_: TRootState, id: number) => id,
    makeInitialTtsItemById(),
    (_, initialTtsItem): string => initialTtsItem?.text || ''
  )

const makeInitialTtsItemSpeakerIdById = () =>
  createSelector(
    (_: TRootState, id: number) => id,
    makeInitialTtsItemById(),
    (_, initialTtsItem): number =>
      initialTtsItem?.speakerId || TTS_DEFAULT_SPEAKER_ID
  )
const makeInitialTtsItemLangCodeById = () =>
  createSelector(
    (_: TRootState, id: number) => id,
    makeInitialTtsItemById(),
    (_, initialTtsItem): string =>
      initialTtsItem?.langCode || TTS_DEFAULT_LANG_CODE
  )

const makeInitialTtsItemInstructionById = () =>
  createSelector(
    (_: TRootState, id: number) => id,
    makeInitialTtsItemById(),
    (_, initialTtsItem): string =>
      initialTtsItem?.instructions || TTS_DEFAULT_INSTRUCTION
  )

const makeInitialTtsItemSpeedById = () =>
  createSelector(
    (_: TRootState, id: number) => id,
    makeInitialTtsItemById(),
    (_, initialTtsItem): number => initialTtsItem?.speed || TTS_DEFAULT_SPEED
  )

const makeTtsItemSpeakerIdById = () =>
  createSelector(
    (_: TRootState, id: number) => id,
    makeTtsItemById(),
    (_, ttsItem): number => ttsItem?.data.speakerId || TTS_DEFAULT_SPEAKER_ID
  )

const makeTtsItemLangCodeById = () =>
  createSelector(
    (_: TRootState, id: number) => id,
    makeTtsItemById(),
    (_, ttsItem): string => ttsItem?.data?.langCode || TTS_DEFAULT_LANG_CODE
  )

const makeTtsItemInstructionById = () =>
  createSelector(
    (_: TRootState, id: number) => id,
    makeTtsItemById(),
    (_, ttsItem): string => ttsItem?.data?.instructions || ''
  )

const makeTtsItemSpeedById = () =>
  createSelector(
    (_: TRootState, id: number) => id,
    makeTtsItemById(),
    (_, ttsItem): number => ttsItem?.data?.speed || TTS_DEFAULT_SPEED
  )

const makeTtsItemTextValueById = () =>
  createSelector(
    (_: TRootState, id: number) => id,
    makeTtsItemById(),
    (_, ttsItem): string => ttsItem?.data.text || ''
  )

const isTtsItemTextEmpty = createSelector(
  makeTtsItemTextValueById(),
  (text): boolean => !text
)

const isAnyTtsItemIsLoading = createSelector(ttsItems, (items): boolean =>
  items.some(item => item.isLoading)
)

const isAllTtsItemsGenerated = createSelector(ttsItems, (items): boolean =>
  items.every(item => item.isGenerated)
)

// selecting all the items that are already generated with the new selected speakerId
// or speed for bulk actions to not regenearte them if the text is not changed also

const ttsUnchangedItems = createSelector(
  (
    _: TRootState,
    payload: {
      speakerId: number
      speed: number
      langCode: string
      instructions: string
    }
  ) => payload,
  ttsInitialItems,
  ttsItems,
  (payload, ttsInitialItems, ttsItems): Array<number> => {
    return ttsItems
      .filter(ttsItem =>
        getIsTtsItemUnchanged({
          ttsItem,
          initialTtsItems: ttsInitialItems,
          ...payload
        })
      )
      .map(ttsItem => ttsItem?.data.id)
  }
)

const notGeneratedAndNotEmptyTtsItems = createSelector(
  ttsItems,
  (items): ITtsItem[] => {
    return items.filter(item => !item.isGenerated && item.data.text !== '')
  }
)
const previouslyGeneratedUnchangedItems = createSelector(
  ttsItems,
  (items): ITtsItem[] => items.filter(item => !item.isDraft && item.isGenerated)
)

const isTtsGenerateActionDisabled = createSelector(
  isAnyTtsItemIsLoading,
  notGeneratedAndNotEmptyTtsItems,
  (isLoading, notGeneratedAndNotEmptyTtsItems): boolean =>
    isLoading || !notGeneratedAndNotEmptyTtsItems.length
)

const isTtsItemsSpeedValuesMixed = createSelector(ttsItems, (items): boolean =>
  items.some(item => item.data.speed !== items[0].data.speed)
)

const isTtsItemsLangCodesMixed = createSelector(ttsItems, (items): boolean =>
  items.some(item => item.data.langCode !== items[0].data.langCode)
)

const isTtsItemsSpeakerIdsMixed = createSelector(
  ttsItems,
  isTtsItemsLangCodesMixed,
  (items, isTtsItemsLangCodesMixed): boolean =>
    items.some(item => item.data.speakerId !== items[0]?.data.speakerId) ||
    isTtsItemsLangCodesMixed
)

const isTtsItemsSpeakerToneInstructionsMixed = createSelector(
  ttsItems,
  (items): boolean =>
    items.some(item => item.data.instructions !== items[0].data.instructions)
)

const ttsItemsLastItem = createSelector(
  ttsItems,
  (items): ITtsItemData => items[items.length - 1]?.data
)

const newTtsItemProps = createSelector(ttsItemsLastItem, ttsItem => {
  return {
    speakerId: ttsItem?.speakerId || TTS_DEFAULT_SPEAKER_ID,
    speed: ttsItem?.speed || TTS_DEFAULT_SPEED,
    langCode: ttsItem?.langCode || TTS_DEFAULT_LANG_CODE,
    instructions: ttsItem?.instructions || TTS_DEFAULT_INSTRUCTION
  }
})

const isTtsTabActive = createSelector(
  editorSelectors.activeSidebarTab,
  (activeSidebarTab): boolean => activeSidebarTab === SUB_SIDEBAR_KEYS.TTS
)

const isNotGeneratedTtsPopupVisible = createSelector(
  isTtsTabActive,
  isAllTtsItemsGenerated,
  (isTtsTabActive: boolean, isAllItemsGenerated: boolean): boolean => {
    return isTtsTabActive && !isAllItemsGenerated
  }
)

const ttsCreditLimitInSeconds = createSelector(
  selector,
  (state): number | null => state.ttsCredits?.data // in seconds
)

const isTtsCreditLimitTerminated = createSelector(
  ttsCreditLimitInSeconds,
  (ttsCreditLimitInSeconds): boolean => ttsCreditLimitInSeconds === 0
)

const isTtsCreditLimitExpiring = createSelector(
  [ttsCreditLimitInSeconds, isTtsCreditLimitTerminated],
  (ttsCreditLimitInSeconds, isTtsCreditLimitTerminated): boolean =>
    !!ttsCreditLimitInSeconds &&
    ttsCreditLimitInSeconds <= SECONDS_IN_MINUTE &&
    !isTtsCreditLimitTerminated
)

const ttsCreditLimitInMinutes = createSelector(
  [ttsCreditLimitInSeconds],
  (ttsCreditLimitInSeconds): number =>
    ttsCreditLimitInSeconds
      ? Math.ceil(ttsCreditLimitInSeconds / SECONDS_IN_MINUTE)
      : 0
)

const isTtsCreditLimitAvailable = createSelector(
  ttsCreditLimitInSeconds,
  (ttsCreditLimitInSeconds): boolean =>
    !!ttsCreditLimitInSeconds && ttsCreditLimitInSeconds > SECONDS_IN_MINUTE
)

const isTtsCreditLimitLoading = createSelector(
  selector,
  (state): boolean => state.ttsCredits.isLoading
)
const isTtsGenerationFailed = createSelector(
  selector,
  (state): boolean => state.ttsGenerationStatus === TTS_GENERATION_STATUS.error
)

const ttsBulkSpeakerIdValue = createSelector(
  isTtsItemsSpeakerIdsMixed,
  ttsItemsLastItem,
  (isMixed: boolean, ttsItemsLastItem): number =>
    determineBulkValue({
      isMixed,
      lastValue: ttsItemsLastItem?.speakerId,
      defaultValue: TTS_DEFAULT_BULK_SPEAKER_ID
    })
)

const ttsBulkSpeedValue = createSelector(
  isTtsItemsSpeedValuesMixed,
  ttsItemsLastItem,
  (isMixed: boolean, ttsItemsLastItem): number =>
    determineBulkValue({
      isMixed,
      lastValue: ttsItemsLastItem?.speed,
      defaultValue: TTS_BULK_DEFAULT_SPEED
    })
)

const ttsBulkLangCodeValue = createSelector(
  isTtsItemsLangCodesMixed,
  ttsItemsLastItem,
  (isMixed: boolean, ttsItemsLastItem): string =>
    determineBulkValue({
      isMixed,
      lastValue: ttsItemsLastItem?.langCode,
      defaultValue: TTS_DEFAULT_BULK_LANG_CODE
    })
)

const ttsBulkToneInstructionValue = createSelector(
  isTtsItemsSpeakerToneInstructionsMixed,
  ttsItemsLastItem,
  (isMixed: boolean, ttsItemsLastItem): string =>
    determineBulkValue({
      isMixed,
      lastValue: ttsItemsLastItem?.instructions,
      defaultValue: TTS_DEFAULT_BULK_INSTRUCTION
    })
)

const ttsBulkValues = createSelector(
  ttsBulkSpeedValue,
  ttsBulkSpeakerIdValue,
  ttsBulkLangCodeValue,
  ttsBulkToneInstructionValue,
  (speed, speakerId, langCode, instructions) => ({
    speed,
    speakerId,
    langCode,
    instructions
  })
)

const ttsCredits = createSelector(
  selector,
  (state): number => state.ttsCredits?.data || 0
)

const isTtsCreditsLoading = createSelector(
  selector,
  (state): boolean => state.ttsCredits?.isLoading || false
)

const ttsGenerationStatus = createSelector(
  selector,
  (state): TTtsGenerationStatus | null => state.ttsGenerationStatus
)

const newGeneratedItemsInitialIdAndStatus = createSelector(
  selector,
  (state): INewGeneratedItemsInitialIdAndStatus[] =>
    state.newGeneratedItemsInitialIdAndStatus
)

const notGeneratedOrDraftTtsItems = createSelector(
  ttsItems,
  (ttsItems): ITtsItem[] =>
    ttsItems.filter(item => !item.isGenerated || item.isDraft)
)

const selectAndPrepareStateForSync = (state: TRootState) => {
  return {
    sortedTtsAudiosIds: getSortedTtsItemIdsFromAudios(
      projectDataSelectors.ttsAudios(state)
    ),
    notGeneratedOrDraftTtsItems: notGeneratedOrDraftTtsItems(state),
    previouslyGeneratedUnchangedItems: previouslyGeneratedUnchangedItems(state),
    newGeneratedItemsInitialIdAndStatus:
      newGeneratedItemsInitialIdAndStatus(state)
  }
}

const isGenerationInProcess = createSelector(
  ttsGenerationStatus,
  (ttsGenerationStatus): boolean =>
    ttsGenerationStatus === TTS_GENERATION_STATUS.processing
)

const isAnyPremiumVoiceSelected = createSelector(
  ttsItems,
  speakersPerLangCode,
  (ttsItems, speakersPerLangCode): boolean =>
    ttsItems
      .filter(item => !item.isGenerated)
      .some(item => checkIfPremiumVoiceSelected(item, speakersPerLangCode))
)

const isTtsMismatchPopupVisible = createSelector(
  selector,
  (state): boolean => state.isTtsMismatchPopupVisible
)

export const ttsSelectors = {
  previouslyGeneratedUnchangedItems,
  ttsBulkValues,
  ttsInitialItems,
  isTtsInitialItemsLoading,
  ttsItems,
  isTtsItemsLoading,
  speakerTones,
  isSpeakerTonesLoading,
  languageOptionsData,
  isLanguageOptionsLoading,
  speakersPerLangCode,
  speakersPerLangCodeSelector,
  speakersPerLangCodeIsLoadingSelector,
  newTtsItemProps,
  makeTtsItemById,
  makeTtsItemDraftStateById,
  isTtsTabActive,
  isNotGeneratedTtsPopupVisible,
  ttsCreditLimitInSeconds,
  isTtsCreditLimitTerminated,
  isTtsCreditLimitExpiring,
  ttsCreditLimitInMinutes,
  isTtsCreditLimitAvailable,
  isTtsCreditLimitLoading,
  ttsBulkSpeakerIdValue,
  ttsBulkSpeedValue,
  ttsBulkLangCodeValue,
  ttsBulkToneInstructionValue,
  ttsUnchangedItems,
  notGeneratedAndNotEmptyTtsItems,
  isTtsItemsSpeakerIdsMixed,
  isTtsItemsSpeedValuesMixed,
  isTtsItemsLangCodesMixed,
  isTtsItemsSpeakerToneInstructionsMixed,
  ttsItemsLastItem,
  isTtsGenerateActionDisabled,
  isSingleDraftTtsItemOnly,
  makeTtsItemLoadingStateById,
  makeTtsItemGeneratedStateById,
  makeInitialTtsItemTextById,
  makeInitialTtsItemSpeakerIdById,
  makeInitialTtsItemLangCodeById,
  makeInitialTtsItemInstructionById,
  makeInitialTtsItemSpeedById,
  makeTtsItemSpeakerIdById,
  makeTtsItemLangCodeById,
  makeTtsItemInstructionById,
  makeTtsItemSpeedById,
  makeTtsItemTextValueById,
  makeTtsItemOtherKeysAlreadyChanged,
  isTtsGenerationFailed,
  isTtsItemTextEmpty,
  isAnyTtsItemIsLoading,
  isAllTtsItemsGenerated,
  ttsCredits,
  isTtsCreditsLoading,
  ttsGenerationStatus,
  isGenerationInProcess,
  newGeneratedItemsInitialIdAndStatus,
  notGeneratedOrDraftTtsItems,
  selectAndPrepareStateForSync,
  isAnyPremiumVoiceSelected,
  isTtsMismatchPopupVisible
}
